"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from "react";
import { ApiWaveCreditScope } from "@/generated/models/ApiWaveCreditScope";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import MyStreamWaveMyVote from "./MyStreamWaveMyVote";
import { useLayout } from "../layout/LayoutContext";
import { WaveLeaderboardLoadingBar } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoadingBar";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import MyStreamWaveMyVotesReset from "./MyStreamWaveMyVotesReset";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
interface MyStreamWaveMyVotesProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveMyVotes: React.FC<MyStreamWaveMyVotesProps> = t0 => {
  const $ = _c(41);
  const {
    wave,
    onDropClick
  } = t0;
  const [isResettingVotes, setIsResettingVotes] = useState(false);
  const t1 = !isResettingVotes;
  let t2;
  if ($[0] !== t1 || $[1] !== wave.id) {
    t2 = {
      waveId: wave.id,
      sort: WaveDropsLeaderboardSort.MY_REALTIME_VOTE,
      enabled: t1
    };
    $[0] = t1;
    $[1] = wave.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveDropsLeaderboard(t2);
  let t3;
  if ($[3] !== wave) {
    t3 = {
      wave
    };
    $[3] = wave;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    isVotingControlsLocked
  } = useApprovalWaveStatus(t3);
  const {
    myVotesViewStyle
  } = useLayout();
  const creditScope = (wave as Partial<ApiWave>).voting?.credit_scope ?? ApiWaveCreditScope.Wave;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Set();
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [checkedDrops, setCheckedDrops] = useState(t4);
  let t5;
  bb0: {
    let t6;
    if ($[6] !== drops) {
      t6 = drops.find(_temp);
      $[6] = drops;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    const dropWithContext = t6;
    if (!dropWithContext) {
      t5 = null;
      break bb0;
    }
    const currentVoteValue = dropWithContext.context_profile_context?.rating ?? 0;
    const maxRating = dropWithContext.context_profile_context?.max_rating;
    if (typeof maxRating !== "number") {
      t5 = null;
      break bb0;
    }
    t5 = Math.max(0, maxRating - currentVoteValue);
  }
  const sharedAvailableVotes = t5;
  let t6;
  bb1: {
    if (isVotingControlsLocked) {
      t6 = false;
      break bb1;
    }
    let t7;
    if ($[8] !== checkedDrops || $[9] !== drops) {
      t7 = !!drops.length && drops.every(drop_0 => checkedDrops.has(drop_0.id));
      $[8] = checkedDrops;
      $[9] = drops;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    t6 = t7;
  }
  const allItemsSelected = t6;
  let t7;
  if ($[11] !== isVotingControlsLocked) {
    t7 = dropId => {
      if (isVotingControlsLocked) {
        return;
      }
      setCheckedDrops(prev => {
        const next = new Set(prev);
        if (next.has(dropId)) {
          next.delete(dropId);
        } else {
          next.add(dropId);
        }
        return next;
      });
    };
    $[11] = isVotingControlsLocked;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleToggleCheck = t7;
  let t8;
  if ($[13] !== allItemsSelected || $[14] !== drops || $[15] !== isVotingControlsLocked) {
    t8 = () => {
      if (isVotingControlsLocked) {
        return;
      }
      if (allItemsSelected) {
        setCheckedDrops(new Set());
      } else {
        setCheckedDrops(new Set(drops.map(_temp2)));
      }
    };
    $[13] = allItemsSelected;
    $[14] = drops;
    $[15] = isVotingControlsLocked;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleToggleSelectAll = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = dropId_0 => {
      setCheckedDrops(prev_0 => {
        const next_0 = new Set(prev_0);
        next_0.delete(dropId_0);
        return next_0;
      });
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const removeSelected = t9;
  let t10;
  if ($[18] !== fetchNextPage || $[19] !== hasNextPage || $[20] !== isFetching || $[21] !== isFetchingNextPage) {
    t10 = () => {
      if (hasNextPage && !isFetching && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[18] = fetchNextPage;
    $[19] = hasNextPage;
    $[20] = isFetching;
    $[21] = isFetchingNextPage;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const intersectionElementRef = useIntersectionObserver(t10);
  let t11;
  if ($[23] !== allItemsSelected || $[24] !== checkedDrops || $[25] !== creditScope || $[26] !== drops || $[27] !== handleToggleCheck || $[28] !== handleToggleSelectAll || $[29] !== intersectionElementRef || $[30] !== isFetching || $[31] !== isFetchingNextPage || $[32] !== isResettingVotes || $[33] !== isVotingControlsLocked || $[34] !== onDropClick || $[35] !== sharedAvailableVotes || $[36] !== wave.id) {
    t11 = drops.length === 0 && !isFetching ? <div className="tw-mt-10"><p className="tw-text-center tw-text-sm tw-text-iron-500">You haven't voted on any submissions in this wave yet.</p></div> : <div className="tw-mt-4 tw-space-y-4">{!isVotingControlsLocked && <MyStreamWaveMyVotesReset waveId={wave.id} haveDrops={!!drops.length} availableVotes={creditScope === ApiWaveCreditScope.Wave ? sharedAvailableVotes : null} selected={checkedDrops} allItemsSelected={allItemsSelected} isVotingClosed={isVotingControlsLocked} onToggleSelectAll={handleToggleSelectAll} removeSelected={removeSelected} onResettingChange={setIsResettingVotes} />}<div className="tw-space-y-2">{drops.map(drop_2 => <MyStreamWaveMyVote key={drop_2.id} drop={drop_2} onDropClick={onDropClick} isChecked={!isVotingControlsLocked && checkedDrops.has(drop_2.id)} isResetting={isResettingVotes} isVotingClosed={isVotingControlsLocked} onToggleCheck={handleToggleCheck} />)}{isFetchingNextPage && <WaveLeaderboardLoadingBar />}<div ref={intersectionElementRef} /></div></div>;
    $[23] = allItemsSelected;
    $[24] = checkedDrops;
    $[25] = creditScope;
    $[26] = drops;
    $[27] = handleToggleCheck;
    $[28] = handleToggleSelectAll;
    $[29] = intersectionElementRef;
    $[30] = isFetching;
    $[31] = isFetchingNextPage;
    $[32] = isResettingVotes;
    $[33] = isVotingControlsLocked;
    $[34] = onDropClick;
    $[35] = sharedAvailableVotes;
    $[36] = wave.id;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== myVotesViewStyle || $[39] !== t11) {
    t12 = <div className="tw-space-y-4 tw-overflow-y-auto tw-px-2 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-px-4 lg:tw-space-y-6" style={myVotesViewStyle}>{t11}</div>;
    $[38] = myVotesViewStyle;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
};
export default MyStreamWaveMyVotes;
function _temp(drop) {
  return drop.context_profile_context;
}
function _temp2(drop_1) {
  return drop_1.id;
}